// steps/UserSigninCredentialsStep.tsx

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import ErrorMessage from '@/components/common/ErrorMessage';
import CustomPasswordInput from '@/components/form/CustomPasswordInput';
import CustomTextInput from '@/components/form/CustomTextInput';

import { useApp } from '@/contexts/AppContext';

import { BasicResponse } from '@/core/models/common-types';
import { extractErrorMessage } from '@/core/utils/utils';

import { UserSigninCredentialsFormValues, userSigninCredentialsSchema } from '../schemas/userSigninCredentialsSchema';

import { UserSigninAction, UserSigninState } from '../states/userSigninReducer';
import axios from 'axios';
import CustomTurnstile from '@/components/form/CustomTurnstile';

interface UserSigninCredentialsStepProps {
  onNext: (data: UserSigninCredentialsFormValues) => void;
  state: UserSigninState;
  dispatch: React.Dispatch<UserSigninAction>;
}

const UserSigninCredentialsStep: React.FC<UserSigninCredentialsStepProps> = ({ onNext, state, dispatch }) => {
  const { isLoading, baseUrl } = useApp();
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);
  const [error, setError] = useState<BasicResponse | null>(null);

  const { control, handleSubmit } = useForm<UserSigninCredentialsFormValues>({
    resolver: zodResolver(userSigninCredentialsSchema),
    defaultValues: {
      userName: state.userName ?? '',
      password: state.password ?? '',
    },
  });

  const submitForm = async (formValues: UserSigninCredentialsFormValues) => {
    try {
      setError(null);
      const { data } = await axios.post(`${baseUrl}/api/v2/UserSignin/StartLogin`, {
        loginSessionId: state.loginSessionId,
        userName: formValues.userName,
        password: formValues.password,
        turnstileToken: formValues.turnstileToken,
      });

      dispatch({
        type: 'UPDATE',
        payload: {
          loginSessionId: data.loginSessionId,
          phoneNumber: data.phoneNumber,
          email: data.email,
        },
      });

      onNext(formValues);
    } catch (err) {
      setError(extractErrorMessage(err));
      setTurnstileResetKey((current) => current + 1);
    }
  };
  return (
    <>
      <ErrorMessage error={error} />

      <form onSubmit={handleSubmit(submitForm)} noValidate>
        <CustomTextInput
          control={control}
          name="userName"
          label="Username"
          placeholder="Enter National ID or Passport number"
          required
          autoComplete={'username'}
        />

        <CustomPasswordInput control={control} name="password" label="Password" placeholder="Enter Password" required />
        <a href="/auth/forgot-password" className="link-primary text-decoration-none small fw-semibold">
          Forgot your password?
        </a>
        <CustomTurnstile name="turnstileToken" control={control} action="signin" resetKey={turnstileResetKey} />

        <div className="d-grid mt-1">
          <button type="submit" className="btn btn-primary  shadow-sm" disabled={isLoading} aria-busy={isLoading}>
            {isLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" aria-hidden="true" />
                Signing in...
              </>
            ) : (
              'Sign In'
            )}
          </button>
        </div>
      </form>
    </>
  );
};

export default UserSigninCredentialsStep;
